import type { FC } from 'react';
import styled from 'styled-components';
import { useTransitionVisibility } from '../../../lib/hooks/useTransitionVisibility';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';

type SwitchViewMenuProps = {
  selectedMenu: string | undefined;
  onSwitch: (view: ViewOptions) => void;
  isManagerView?: boolean;
};

export enum ViewOptions {
  MANAGER,
  USER,
}

export const SwitchViewMenu: FC<SwitchViewMenuProps> = ({
  selectedMenu,
  onSwitch,
  isManagerView = false,
}: SwitchViewMenuProps) => {
  const visible = selectedMenu === 'switchView';
  const { ref, isMounted } = useTransitionVisibility<HTMLDivElement>(visible);

  return (
    isMounted && (
      <MenuContainer $isHidden={!visible} ref={ref}>
        <MenuItem $isActive={isManagerView} $isManagerTone onClick={() => onSwitch(ViewOptions.MANAGER)}>
          <NamedContentEntryText refKey="menuSystem" container="span" subKey="managerView" />
        </MenuItem>
        <MenuItem $isActive={!isManagerView} onClick={() => onSwitch(ViewOptions.USER)}>
          <NamedContentEntryText refKey="menuSystem" container="span" subKey="userView" />
        </MenuItem>
      </MenuContainer>
    )
  );
};

const MenuContainer = styled.div<{
  $isHidden: boolean;
}>`
  opacity: ${({ $isHidden: isHidden }) => (isHidden ? 0 : 1)};
  transform: ${({ $isHidden: isHidden }) => (isHidden ? 'translateY(-5px)' : 'translateY(0)')};
  transition:
    opacity 0.3s,
    transform 0.3s;
  pointer-events: ${({ $isHidden: isHidden }) => (isHidden ? 'none' : 'auto')};
  width: 208px;
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  background-color: ${({ theme }) => theme.colors.prototype.card};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  box-shadow: ${({ theme }) => theme.colors.topBarShadow};
  border: 1px solid ${({ theme }) => theme.colors.topBarBorder};
  overflow: hidden;
  z-index: 99;

  @media (max-width: 768px) {
    width: 232px;
  }
`;

const MenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$isActive' && prop !== '$isManagerTone',
})<{ $isActive: boolean; $isManagerTone?: boolean }>`
  width: 100%;
  border: none;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 12px;
  text-align: left;
  border-radius: 0;
  background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.topBarHoverBackground : 'transparent')};
  color: ${({ theme, $isActive, $isManagerTone }) =>
    $isActive
      ? $isManagerTone
        ? theme.colors.prototype.accent
        : theme.colors.prototype.userPrimary
      : theme.colors.prototype.foreground};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 500)};

  &:hover {
    background-color: ${({ theme }) => theme.colors.topBarHoverBackground};
  }
`;
